import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { fehlendeFunktion } from '../billtoro/upgrade';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { isBilltoro } from '../produkt';
import { ChipRadios } from './ChipRadios';
import { empfehlung, isHourly } from './empfehlung';
import { deQty, dePrice, emptyRow, kundeWerteOf, LEERER_KUNDE, parsePrice, ratesFor, summen, type KundeWerte } from './formular';
import { suche } from './KundeWahl';
import { useLeistungen } from './LeistungDaten';
import type { EntwurfDaten } from './RechnungForm';
import { STAPEL, stapelLesen, verkleinern, type GelesenerZettel, type StapelGruppe, type StapelZettel } from './StapelDaten';
import { nettoCents, stundenPositionen, zeitraum, zeitraumText, type PositionsArt } from './stundenPositionen';
import { darf, fehlerText } from './tarifHinweis';
import type { Kunde, Overview, Rate } from './types';
import { VorschlagInput } from './Vorschlaege';
import './formular.css';
import './stundenzettel-stapel.css';

// Mehrere Stundenzettel auf einmal (Sam 13.10.: „für 20 objekte die stundenzettel packe ich rein und der erstellt dann
// die rechnungen für alle“). Hochladen (Fotos oder PDFs, auch eine PDF mit vielen Zetteln), die KI liest, die App macht
// je Objekt eine Rechnung und schlägt den Kunden aus der Kundenliste vor. Auf der Prüf-Seite: Haken, Kunde, Stundensatz;
// steht auf Zetteln kein Empfänger, fragt die Seite, ob alle an denselben Kunden gehen. Dann je Rechnung ein normaler
// Entwurf (Vermerk „aus Stundenzettel“), geschrieben und verschickt wird erst im Formular.
// Billtoro: Danach schickt die Seite die Dateien noch einmal, und der Server legt jeden Zettel beim Kunden unter
// „Dateien“ ab, je Objekt ein Ordner, verbunden mit dem Entwurf (stundenzettel-ablage.ts). Klappt das nicht für alle,
// bleiben die Entwürfe, und die Seite bietet „Noch einmal ablegen“ an.

const BILLTORO = isBilltoro();

const MB = 1024 * 1024;
const ARTEN: [PositionsArt, string][] = [
  ['taetigkeit', 'Je Tätigkeit'],
  ['einzeln', 'Je Eintrag'],
  ['summe', 'Alles in einer'],
];
const hours = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
const zettelText = (n: number) => (n === 1 ? '1 Zettel' : `${n} Zettel`);
const entwuerfeText = (n: number) => (n === 1 ? '1 Entwurf' : `${n} Entwürfe`);
const abgelegtText = (n: number) => (n === 1 ? '1 Stundenzettel' : `${n} Stundenzettel`);
const seitenText = (z: StapelZettel) => (z.seiten && (z.seiten[0] > 1 || z.seiten[1] > 1) ? ` S. ${z.seiten[0] === z.seiten[1] ? z.seiten[0] : `${z.seiten[0]}–${z.seiten[1]}`}` : '');
const satzOk = (t: string) => {
  const p = parsePrice(t);
  return typeof p === 'number' && p > 0;
};

// Kunde einer Rechnung: aus der Liste (kundeId) oder ein neuer Name (neu); ki: von der App vorgeschlagen (gelb), bis der
// Mandant ihn ändert
interface KundeWahlWert {
  kundeId: string | null;
  neu: string | null;
}
interface Zeile extends KundeWahlWert {
  on: boolean;
  ki: boolean;
  // eigener Stundensatz dieser Rechnung (null: oben bzw. Empfehlung)
  satz: string | null;
}

// Billtoro: welche Zettel (Datei des Stapels, Seiten) zu welchem Entwurf, wie der Server sie ablegt
interface Zuordnung {
  entwurfId: string;
  teile: { datei: number; seiten: [number, number] | null }[];
}
// Ablegen nicht ganz geklappt: was noch fehlt (zum Wiederholen), titel je Entwurf für die Meldung; wartend: abgelegt,
// aber noch ohne Kunden (Entwurf an einen neuen Kunden)
interface Ablage {
  angelegt: number;
  abgelegt: number;
  wartend: number;
  offen: Zuordnung[];
  fehler: { entwurfId: string; grund: string }[];
  allgemein: string | null;
  titel: Record<string, string>;
}

export function StundenzettelStapel({ data }: { data: Overview }) {
  const navigate = useNavigate();
  const toast = useToast();
  const leistungen = useLeistungen(data.firm.id);
  const ku = data.absender.kleinunternehmer;
  const input = useRef<HTMLInputElement>(null);
  const abort = useRef<AbortController | null>(null);
  const [phase, setPhase] = useState<'wahl' | 'lesen' | 'pruefen'>('wahl');
  const [drag, setDrag] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // vor dem Hochladen aussortiert (Art, Größe)
  const [vorab, setVorab] = useState<{ name: string; fehler: string }[]>([]);
  const [gesamt, setGesamt] = useState<number | null>(null);
  const [zettel, setZettel] = useState<StapelZettel[]>([]);
  const [gruppen, setGruppen] = useState<StapelGruppe[]>([]);
  const [zeilen, setZeilen] = useState<Record<string, Zeile>>({});
  const [satz, setSatz] = useState('');
  const [satzGetippt, setSatzGetippt] = useState(false);
  const [art, setArt] = useState<PositionsArt>('taetigkeit');
  // Namen der Mitarbeiter nur, wenn der Kunde das verlangt
  const [namen, setNamen] = useState(false);
  const [frage, setFrage] = useState<'offen' | 'ja' | 'nein'>('offen');
  const [frageKunde, setFrageKunde] = useState<KundeWahlWert>({ kundeId: null, neu: null });
  const [busy, setBusy] = useState(false);
  // Billtoro: die Dateien des Stapels in der Reihenfolge des Servers, zum Ablegen nach „Entwürfe anlegen“
  const stapelDateien = useRef<File[]>([]);
  const [ablegend, setAblegend] = useState(false);
  const [ablage, setAblage] = useState<Ablage | null>(null);
  // Zettel beim Kunden ablegen: Funktion im Tarif und Recht auf Kontakte (eine Datei am Kunden)
  const ablegenGeht = BILLTORO && !fehlendeFunktion(data.tarif, 'kontaktdateien') && darf(data.tarif, 'kunden');
  const ohneRecht = BILLTORO && !fehlendeFunktion(data.tarif, 'kontaktdateien') && !darf(data.tarif, 'kunden');

  useEffect(() => () => abort.current?.abort(), []);

  const kunden = useMemo(() => new Map(data.kunden.filter((k) => k.id).map((k) => [k.id!, k])), [data.kunden]);

  // Stundensatz oben: aus der Leistungsliste (Leistung in Stunden), sonst aus der letzten Rechnung mit Stunden
  const stundenLeistung = useMemo(() => leistungen.list.find((l) => isHourly(l.unit) && l.price != null) ?? null, [leistungen.list]);
  const vorschlag = useMemo(() => {
    if (stundenLeistung) return { price: dePrice(stundenLeistung.price!), text: `aus Ihrer Leistung „${stundenLeistung.name}“` };
    for (const v of data.verlauf) {
      const i = v.items.find((x) => isHourly(x.unit));
      if (i) return { price: dePrice(i.price), text: `aus Ihrer Rechnung ${v.number} vom ${fmtDate(v.invoiceDate)}` };
    }
    return null;
  }, [stundenLeistung, data.verlauf]);
  useEffect(() => {
    if (!satzGetippt && vorschlag) setSatz(vorschlag.price);
  }, [vorschlag, satzGetippt]);

  async function lesen(list: File[]) {
    setError(null);
    if (!list.length) return;
    if (list.length > STAPEL.dateien) {
      setError(`Das sind ${list.length} Dateien. Höchstens ${STAPEL.dateien} auf einmal, bitte in mehreren Durchgängen hochladen.`);
      return;
    }
    const ok: File[] = [];
    const weg: { name: string; fehler: string }[] = [];
    for (const f of list) {
      if (!/^(application\/pdf|image\/(jpeg|png|webp))$/.test(f.type) && !/\.(pdf|jpe?g|png|webp)$/i.test(f.name)) {
        weg.push({ name: f.name, fehler: 'Bitte als PDF oder Foto (JPG, PNG) hochladen.' });
        continue;
      }
      // leer: der Server ließe sie weg, die Nummern der Dateien stimmten dann nicht mehr
      if (f.size === 0) {
        weg.push({ name: f.name, fehler: 'Die Datei ist leer.' });
        continue;
      }
      const g = await verkleinern(f);
      if (g.size > STAPEL.dateiMb * MB) weg.push({ name: f.name, fehler: `Zu groß für die KI (höchstens ${STAPEL.dateiMb} MB je Datei).` });
      else ok.push(g);
    }
    if (!ok.length) {
      setError(weg.map((w) => `${w.name}: ${w.fehler}`).join(' '));
      return;
    }
    if (ok.reduce((s, f) => s + f.size, 0) > STAPEL.zusammenMb * MB) {
      setError(`Die Dateien sind zusammen zu groß (höchstens ${STAPEL.zusammenMb} MB). Bitte in mehreren Durchgängen hochladen.`);
      return;
    }
    setVorab(weg);
    setZettel([]);
    setGesamt(null);
    stapelDateien.current = ok;
    setPhase('lesen');
    const ctrl = new AbortController();
    abort.current = ctrl;
    let fertig = false;
    try {
      await stapelLesen(
        `/api/rechnungstool/${data.firm.id}/stundenzettel-stapel`,
        ok,
        (e) => {
          if (e.t === 'gesamt') setGesamt(e.zettel);
          else if (e.t === 'zettel') {
            const { t: _t, ...z } = e;
            setZettel((cur) => [...cur, z]);
          } else if (e.t === 'fehler') setError(e.fehler);
          else if (e.t === 'ende') {
            fertig = true;
            pruefen(e.gruppen);
          }
        },
        ctrl.signal,
      );
      if (!fertig) {
        setError((cur) => cur ?? 'Die Verbindung ist abgebrochen. Bitte noch einmal hochladen.');
        setPhase('wahl');
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      setError(fehlerText(err));
      setPhase('wahl');
    } finally {
      if (input.current) input.current.value = '';
    }
  }

  function abbrechen() {
    abort.current?.abort();
    setPhase('wahl');
    setZettel([]);
  }

  function pruefen(list: StapelGruppe[]) {
    const z: Record<string, Zeile> = {};
    for (const g of list) {
      const id = g.zuordnung.kundeId && kunden.has(g.zuordnung.kundeId) ? g.zuordnung.kundeId : null;
      // Empfänger steht da, ist aber nicht in der Kundenliste: als neuer Kunde (Anschrift vom Zettel)
      const neu = !id && g.zuordnung.stand === 'nicht_gefunden' ? (g.empfaenger?.name ?? null) : null;
      z[g.key] = { on: true, kundeId: id, neu, ki: !!(id || neu), satz: null };
    }
    setZeilen(z);
    setGruppen(list);
    setFrage('offen');
    setFrageKunde({ kundeId: null, neu: null });
    setPhase('pruefen');
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    setDrag(false);
    if (phase === 'wahl') void lesen([...e.dataTransfer.files]);
  }

  // ---------- Prüfen ----------

  const gelesen = useMemo(() => new Map(zettel.filter((z): z is GelesenerZettel => z.ok).map((z) => [z.nr, z])), [zettel]);
  const fehler = [...vorab, ...zettel.filter((z) => !z.ok).map((z) => ({ name: `${z.name}${seitenText(z)}`, fehler: (z as { fehler: string }).fehler }))];
  // Rechnungen ohne Empfänger auf dem Zettel: die Frage oben
  const ohne = gruppen.filter((g) => g.zuordnung.stand === 'fehlt');
  const ohneZettel = ohne.reduce((s, g) => s + g.zettel.length, 0);

  const setZeile = (key: string, patch: Partial<Zeile>) => setZeilen((cur) => ({ ...cur, [key]: { ...cur[key]!, ...patch } }));
  const kundeOf = (z: KundeWahlWert): Kunde | null => (z.kundeId ? (kunden.get(z.kundeId) ?? null) : null);
  const kundeName = (z: KundeWahlWert) => kundeOf(z)?.name ?? z.neu?.trim() ?? '';

  // je Rechnung: Einträge, Stundensatz (eigener, Empfehlung aus alten Rechnungen für dieses Objekt bzw. diesen Kunden, oben),
  // Positionen und Betrag
  const rechnungen = gruppen.map((g) => {
    const z = zeilen[g.key] ?? { on: true, kundeId: null, neu: null, ki: false, satz: null };
    const list = g.zettel.map((nr) => gelesen.get(nr)).filter((x): x is GelesenerZettel => !!x);
    const eintraege = list.flatMap((x) => x.eintraege);
    const hinweise = [...new Set(list.map((x) => x.hinweis).filter((h): h is string => !!h))];
    const k = kundeOf(z);
    const emp = empfehlung(data.verlauf, { name: kundeName(z), street: k?.street ?? '', site: g.objekt ?? '' });
    const empItem = emp?.from.items.find((i) => isHourly(i.unit));
    const empSatz = emp && empItem ? { price: dePrice(empItem.price), text: emp.text } : null;
    const s = z.satz ?? empSatz?.price ?? satz;
    const positionen = satzOk(s) ? stundenPositionen(eintraege, { art, preis: dePrice(parsePrice(s) as number), namen }) : [];
    const netto = positionen.length ? nettoCents(positionen) : null;
    return { g, z, eintraege, hinweise, k, satz: s, empSatz: z.satz == null ? empSatz : null, positionen, netto };
  });
  const gewaehlt = rechnungen.filter((r) => r.z.on);
  const ohneSatz = gewaehlt.filter((r) => !satzOk(r.satz)).length;
  const ohneKunde = gewaehlt.filter((r) => !kundeName(r.z)).length;
  const summe = gewaehlt.reduce((s, r) => s + (r.netto ?? 0), 0);
  const summeStunden = gewaehlt.reduce((s, r) => s + r.g.stunden, 0);

  function alleAnEinen() {
    if (!frageKunde.kundeId && !frageKunde.neu?.trim()) return;
    setZeilen((cur) => {
      const next = { ...cur };
      for (const g of ohne) next[g.key] = { ...next[g.key]!, kundeId: frageKunde.kundeId, neu: frageKunde.kundeId ? null : frageKunde.neu!.trim(), ki: false };
      return next;
    });
    setFrage('ja');
  }

  // Formularstand des Entwurfs wie beim Speichern im Rechnungsformular
  function entwurf(r: (typeof rechnungen)[number]) {
    const { g, z, k } = r;
    const kunde: KundeWerte = k
      ? kundeWerteOf(k)
      : {
          ...LEERER_KUNDE,
          name: z.neu?.trim() ?? '',
          ...(z.neu && z.neu === g.empfaenger?.name
            ? { street: g.empfaenger.strasse ?? '', zip: g.empfaenger.plz ?? '', city: g.empfaenger.ort ?? '' }
            : {}),
        };
    // Steuersatz der Stunden-Leistung (sonst 19 %)
    const lr = stundenLeistung?.rate;
    const rate: Rate = lr !== undefined && ratesFor(ku).includes(lr) ? lr : 19;
    // ki: gelb im Formular, bis der Mandant die Zeile ändert (Vorschlag der KI, wie beim einzelnen Stundenzettel)
    const rows = r.positionen.map((p) => ({ description: p.description, quantity: deQty(p.quantity), unit: p.unit, price: p.price, rate, discount: '', ki: true }));
    const { from, to } = zeitraum(r.eintraege);
    const n = g.zettel.length;
    const daten: EntwurfDaten = {
      v: 1,
      art: 'normal',
      auftrag: '',
      picked: [],
      kunde,
      xrechnung: !!k && (!!k.xrechnung || kunde.art === 'behoerde'),
      rows,
      serviceFrom: from ?? data.today,
      serviceTo: to && to !== from ? to : '',
      paymentDays: String(k?.paymentDays ?? data.absender.paymentDays),
      skonto: null,
      note: '',
      site: g.objekt ?? '',
      hinweis: `Vorschlag der KI aus ${n === 1 ? '1 Stundenzettel' : `${n} Stundenzetteln`}. Bitte Stunden, Stundensatz und Kunde prüfen.`,
      angebotId: null,
    };
    const brutto = summen(
      rows.map((x) => ({ ...emptyRow(), ...x })),
      ku,
    ).gross;
    return { art: 'rechnung', titel: kunde.name.trim().slice(0, 200), betrag: brutto / 100, daten };
  }

  async function anlegen() {
    if (!gewaehlt.length || ohneSatz) return;
    setBusy(true);
    setError(null);
    let res: { angelegt: number; ids: string[] };
    try {
      res = await api.post<{ angelegt: number; ids: string[] }>(`/api/rechnungstool/${data.firm.id}/stundenzettel-stapel/entwuerfe`, {
        entwuerfe: gewaehlt.map(entwurf),
      });
    } catch (err) {
      setError(fehlerText(err));
      setBusy(false);
      return;
    }
    // je Entwurf seine gelesenen Zettel (die ids kommen in der Reihenfolge der Anfrage)
    const zuordnung: Zuordnung[] = ablegenGeht
      ? gewaehlt
          .map((r, i) => ({
            entwurfId: res.ids[i] ?? '',
            teile: r.g.zettel
              .map((nr) => gelesen.get(nr))
              .filter((x): x is GelesenerZettel => !!x)
              .map((x) => ({ datei: x.datei, seiten: x.seiten })),
          }))
          .filter((z) => z.entwurfId && z.teile.length > 0)
      : [];
    if (!zuordnung.length) {
      toast(`${entwuerfeText(res.angelegt)} angelegt`);
      navigate('/rechnung/rechnungen');
      return;
    }
    const titel = Object.fromEntries(gewaehlt.map((r, i) => [res.ids[i] ?? '', r.g.objekt || kundeName(r.z) || 'Ohne Objekt']));
    await ablegen({ angelegt: res.angelegt, abgelegt: 0, wartend: 0, offen: zuordnung, fehler: [], allgemein: null, titel });
  }

  // Zettel beim Kunden ablegen (auch „Noch einmal ablegen“): nur die Dateien, die die Zettel brauchen, neu gezählt
  async function ablegen(a: Ablage) {
    setBusy(true);
    setAblegend(true);
    const alt = [...new Set(a.offen.flatMap((z) => z.teile.map((t) => t.datei)))].sort((x, y) => x - y);
    const neu = new Map(alt.map((d, i) => [d, i]));
    const fd = new FormData();
    fd.append('zuordnung', JSON.stringify(a.offen.map((z) => ({ ...z, teile: z.teile.map((t) => ({ ...t, datei: neu.get(t.datei)! })) }))));
    for (const d of alt) {
      const f = stapelDateien.current[d];
      if (f) fd.append('dateien', f, f.name);
    }
    try {
      const r = await api.post<{ abgelegt: number; wartend: number; uebersprungen: number; fehler: { entwurfId: string; grund: string }[] }>(
        `/api/rechnungstool/${data.firm.id}/stundenzettel-stapel/ablegen`,
        fd,
      );
      const abgelegt = a.abgelegt + r.abgelegt;
      const wartend = a.wartend + r.wartend;
      if (!r.fehler.length) {
        // Zettel für einen neuen Kunden liegen erst am Entwurf (beim Kunden, sobald die Rechnung geschrieben ist)
        toast(`${entwuerfeText(a.angelegt)} angelegt${abgelegt ? `, ${abgelegtText(abgelegt)} ${wartend ? 'abgelegt' : 'beim Kunden abgelegt'}` : ''}`);
        navigate('/rechnung/rechnungen');
        return;
      }
      const falsch = new Set(r.fehler.map((f) => f.entwurfId));
      setAblage({ ...a, abgelegt, wartend, offen: a.offen.filter((z) => falsch.has(z.entwurfId)), fehler: r.fehler, allgemein: null });
    } catch (err) {
      setAblage({ ...a, fehler: [], allgemein: fehlerText(err) });
    }
    setBusy(false);
    setAblegend(false);
  }

  if (!data.ki) {
    return (
      <div className="rt-card sz">
        <h1>Stundenzettel hochladen</h1>
        <div className="notice info">Die KI ist für Ihre Firma nicht eingerichtet. Stunden bitte in der Rechnung von Hand eintragen.</div>
        <div className="rt-actions">
          <Link to="/rechnung/rechnungen" className="btn">
            Zurück
          </Link>
        </div>
      </div>
    );
  }

  const testHinweis = data.kiTest && (
    <p className="rt-ki-test" role="note">
      Testbetrieb: Die KI läuft gerade nicht nur in der EU. Bitte nur Test-Stundenzettel hochladen, keine echten.
    </p>
  );

  // Entwürfe sind angelegt, aber nicht alle Zettel abgelegt
  if (ablage) {
    return (
      <div className="rt-card sz">
        <h1>Stundenzettel ablegen</h1>
        <div className="notice ok">{entwuerfeText(ablage.angelegt)} angelegt.</div>
        <div className="notice bad sz-fehler" role="alert">
          <b>{ablage.abgelegt ? `${abgelegtText(ablage.abgelegt)} abgelegt, nicht alle.` : 'Die Stundenzettel sind nicht abgelegt.'}</b>
          {ablage.allgemein ? (
            <p>{ablage.allgemein}</p>
          ) : (
            <ul>
              {ablage.fehler.map((f) => (
                <li key={f.entwurfId}>
                  {ablage.titel[f.entwurfId] ?? 'Entwurf'}: {f.grund}
                </li>
              ))}
            </ul>
          )}
          Die Entwürfe bleiben, es fehlen nur die Zettel beim Kunden.
        </div>
        <div className="rt-actions">
          <Link to="/rechnung/rechnungen" className="btn">
            Zu den Rechnungen
          </Link>
          <button type="button" className="btn primary" disabled={busy || !ablage.offen.length} onClick={() => void ablegen(ablage)}>
            {ablegend ? 'Stundenzettel werden abgelegt …' : 'Noch einmal ablegen'}
          </button>
        </div>
      </div>
    );
  }

  if (phase === 'wahl') {
    return (
      <div
        className="rt-card sz"
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDrag(false);
        }}
        onDrop={onDrop}
      >
        <div className="rt-form-head">
          <h1>Stundenzettel hochladen</h1>
          <Link to="/rechnung/rechnungen" className="btn small">
            Abbrechen
          </Link>
        </div>
        <p className="muted sz-intro">
          Alle Stundenzettel auf einmal: Fotos oder PDFs, auch eine PDF mit vielen Zetteln hintereinander. Die KI liest sie, die App macht je
          Objekt einen Rechnungsentwurf. Sie prüfen alles, bevor etwas rausgeht.
          {ablegenGeht && ' Die Stundenzettel legt die App danach beim Kunden unter Dateien ab, je Objekt in einem eigenen Ordner.'}
        </p>
        {testHinweis}
        <div className={`sz-drop${drag ? ' on' : ''}`}>
          <button type="button" className="btn primary" onClick={() => input.current?.click()}>
            Dateien auswählen
          </button>
          <span className="sz-drop-t muted">oder hierher ziehen</span>
          <small className="muted">
            Bis zu {STAPEL.dateien} Dateien und {STAPEL.seiten} Seiten auf einmal, je Datei bis {STAPEL.dateiMb} MB.
          </small>
          <input
            ref={input}
            type="file"
            multiple
            hidden
            aria-label="Stundenzettel auswählen"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            onChange={(e) => void lesen([...(e.target.files ?? [])])}
          />
        </div>
        {error && <div className="notice bad">{error}</div>}
      </div>
    );
  }

  if (phase === 'lesen') {
    const fertig = zettel.length;
    return (
      <div className="rt-card sz">
        <h1>Stundenzettel werden gelesen</h1>
        {testHinweis}
        <p className="sz-prog-t" aria-live="polite">
          {gesamt == null ? 'Die Dateien werden vorbereitet …' : `${fertig} von ${gesamt} gelesen`}
        </p>
        <progress className="sz-prog" max={gesamt ?? 1} value={gesamt == null ? undefined : fertig} aria-label="Fortschritt" />
        {zettel.length > 0 && (
          <ul className="sz-gel">
            {zettel.map((z) => (
              <li key={z.nr} className={z.ok ? 'ok' : 'bad'}>
                <span aria-hidden="true">{z.ok ? '✓' : '!'}</span>
                <span>
                  {z.name}
                  {seitenText(z)}
                  {z.ok ? (z.objekt ? ` · ${z.objekt}` : '') : ` · ${z.fehler}`}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="muted sz-klein">Sie können die Seite offen lassen, das dauert je Zettel einige Sekunden.</p>
        <div className="rt-actions">
          <button type="button" className="btn" onClick={abbrechen}>
            Abbrechen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-card sz">
      <div className="rt-form-head">
        <h1>Stundenzettel prüfen</h1>
        <Link to="/rechnung/rechnungen" className="btn small">
          Abbrechen
        </Link>
      </div>
      <p className="muted sz-intro">
        {zettelText(gelesen.size)} gelesen, daraus {gruppen.length === 1 ? '1 Rechnung' : `${gruppen.length} Rechnungen`} (je Objekt eine).{' '}
        <span className="sz-ki">Gelb</span> ist von der KI gelesen: bitte prüfen.
      </p>
      {testHinweis}
      {fehler.length > 0 && (
        <div className="notice warn sz-fehler">
          <b>{fehler.length === 1 ? '1 Datei nicht gelesen' : `${fehler.length} Dateien bzw. Zettel nicht gelesen`}</b>
          <ul>
            {fehler.map((f, i) => (
              <li key={i}>
                {f.name}: {f.fehler}
              </li>
            ))}
          </ul>
          Diese bitte noch einmal hochladen oder in der Rechnung von Hand eintragen.
        </div>
      )}

      {gruppen.length === 0 ? (
        <div className="rt-actions">
          <button type="button" className="btn primary" onClick={() => setPhase('wahl')}>
            Andere Dateien hochladen
          </button>
        </div>
      ) : (
        <>
          {ohne.length > 0 && frage === 'offen' && (
            <section className="sz-frage" aria-label="Kein Empfänger">
              <b>
                Auf {ohneZettel === 1 ? '1 Zettel' : `${ohneZettel} Zetteln`} steht kein Empfänger
                {ohne.every((g) => g.objekt) ? ', nur das Bauvorhaben' : ''}. Gehen alle an denselben Kunden?
              </b>
              <p className="muted sz-frage-obj">{ohne.map((g) => g.objekt ?? 'ohne Objekt').join(' · ')}</p>
              <div className="sz-frage-wahl">
                <KundeFeld kunden={data.kunden} wert={frageKunde} onChange={setFrageKunde} label="Kunde für alle" />
              </div>
              <div className="sz-frage-act">
                <button type="button" className="btn primary" disabled={!frageKunde.kundeId && !frageKunde.neu?.trim()} onClick={alleAnEinen}>
                  {frageKunde.kundeId || frageKunde.neu?.trim() ? `Ja, alle an ${kundeName(frageKunde)}` : 'Ja, alle an diesen Kunden'}
                </button>
                <button type="button" className="btn" onClick={() => setFrage('nein')}>
                  Nein, ich wähle je Zettel
                </button>
              </div>
            </section>
          )}

          <div className="sz-kopf">
            <label className="field sz-satz-top">
              <span>Stundensatz netto</span>
              <span className="sz-eur">
                <input
                  value={satz}
                  inputMode="decimal"
                  placeholder="z. B. 32,50"
                  aria-invalid={!!satz.trim() && !satzOk(satz)}
                  onChange={(e) => {
                    setSatz(e.target.value);
                    setSatzGetippt(true);
                  }}
                />
                € je Std.
              </span>
            </label>
            {vorschlag && satz === vorschlag.price && <p className="rt-tip-line sz-quelle">Vorschlag {vorschlag.text}</p>}
            <ChipRadios<PositionsArt> label="Positionen auf der Rechnung" className="sz-art" value={art} options={ARTEN} onChange={setArt} />
            {art === 'einzeln' && (
              <label className="check sz-namen">
                <input type="checkbox" checked={namen} onChange={(e) => setNamen(e.target.checked)} />
                <span>Namen der Mitarbeiter mit auf die Rechnung (nur, wenn Ihr Kunde das verlangt)</span>
              </label>
            )}
          </div>

          <ul className="sz-list">
            {rechnungen.map((r) => {
              const { g, z } = r;
              const titel = g.objekt || kundeName(z) || 'Rechnung';
              return (
                <li key={g.key} className={`sz-row${z.on ? '' : ' off'}`}>
                  <label className="sz-on">
                    <input
                      type="checkbox"
                      checked={z.on}
                      aria-label={`Entwurf für ${titel} anlegen`}
                      onChange={(e) => setZeile(g.key, { on: e.target.checked })}
                    />
                  </label>
                  <div className="sz-obj">
                    <span className="sz-l">Objekt</span>
                    {g.objekt ? <b className="sz-ki">{g.objekt}</b> : <b className="muted">Ohne Objekt</b>}
                    {/* am Handy bricht die Zeile nur zwischen den Teilen um, nie in „28 Std.“ oder „2 Zettel“ */}
                    <small className="muted">
                      {[zeitraumText(g.von, g.bis), `${hours(g.stunden)}\u00a0Std.`, zettelText(g.zettel.length).replace(' ', '\u00a0')]
                        .filter(Boolean)
                        .join(' · ')}
                    </small>
                  </div>
                  <div className="sz-kunde">
                    <span className="sz-l">Kunde</span>
                    <KundeFeld
                      kunden={data.kunden}
                      wert={z}
                      ki={z.ki}
                      label={`Kunde für ${titel}`}
                      onChange={(w) => setZeile(g.key, { ...w, ki: false })}
                    />
                    <KundeStand g={g} z={z} />
                  </div>
                  <label className="sz-satz">
                    <span className="sz-l">Std.-Satz</span>
                    <input
                      value={r.satz}
                      inputMode="decimal"
                      aria-label={`Stundensatz für ${titel}`}
                      aria-invalid={z.on && !satzOk(r.satz)}
                      onChange={(e) => setZeile(g.key, { satz: e.target.value })}
                    />
                  </label>
                  <div className="sz-betrag">
                    <b className="num">{r.netto == null ? '–' : eurC(r.netto / 100)}</b>
                    <small className="muted">netto</small>
                  </div>
                  <div className="sz-mehr">
                    {r.empSatz && <p className="rt-tip-line">Stundensatz: {r.empSatz.text}</p>}
                    {r.hinweise.map((h) => (
                      <p key={h} className="rt-ki-note">
                        KI: {h}
                      </p>
                    ))}
                    {r.positionen.length > 0 && (
                      <details className="sz-pos">
                        <summary>{r.positionen.length === 1 ? '1 Position ansehen' : `${r.positionen.length} Positionen ansehen`}</summary>
                        <ul>
                          {r.positionen.map((p, i) => (
                            <li key={i}>
                              <span>{p.description}</span>
                              <span className="num">
                                {hours(p.quantity)} Std. × {p.price} €
                              </span>
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="sz-fuss">
            <p className="sz-summe">
              <b>{entwuerfeText(gewaehlt.length)}</b> · {hours(summeStunden)} Std. · <b className="num">{eurC(summe / 100)}</b> netto
            </p>
            {ohneSatz > 0 && <p className="rt-warn-line">Bitte den Stundensatz eintragen.</p>}
            {ohneKunde > 0 && !ohneSatz && (
              <p className="muted sz-klein">
                Bei {ohneKunde === 1 ? '1 Entwurf' : `${ohneKunde} Entwürfen`} fehlt noch der Kunde: Sie können ihn auch später im Entwurf eintragen.
              </p>
            )}
            {error && <div className="notice bad">{error}</div>}
            <div className="rt-actions">
              <Link to="/rechnung/rechnungen" className="btn">
                Abbrechen
              </Link>
              <button type="button" className="btn primary" disabled={busy || !gewaehlt.length || ohneSatz > 0} onClick={() => void anlegen()}>
                {ablegend ? 'Stundenzettel werden abgelegt …' : `${entwuerfeText(gewaehlt.length)} anlegen`}
              </button>
            </div>
            <p className="muted sz-klein">
              {ablegenGeht
                ? 'Die Entwürfe finden Sie unter Rechnungen, die Stundenzettel beim Kunden unter Dateien (bei einem neuen Kunden, sobald die Rechnung geschrieben ist).'
                : 'Die Entwürfe finden Sie unter Rechnungen.'}{' '}
              Geschrieben und verschickt wird erst, wenn Sie einen Entwurf öffnen.
              {ohneRecht && ' Die Stundenzettel legt die App nicht beim Kunden ab: Dafür fehlt Ihnen das Recht „Kontakte, Leistungen und Lager“.'}
            </p>
          </div>
        </>
      )}
    </div>
  );
}

// Was die App zum Kunden gefunden hat (solange der Mandant ihn nicht selbst gewählt hat)
function KundeStand({ g, z }: { g: StapelGruppe; z: Zeile }) {
  const s = g.zuordnung.stand;
  if (s === 'fehlt' && !z.kundeId && !z.neu) return <small className="sz-stand">kein Empfänger auf dem Zettel</small>;
  if (!z.ki) return null;
  if (s === 'eindeutig') return <small className="sz-stand ok">passt eindeutig</small>;
  if (s === 'pruefen') return <small className="sz-stand pruefen">bitte prüfen{g.zuordnung.grund ? ` (${g.zuordnung.grund})` : ''}</small>;
  if (s === 'nicht_gefunden') return <small className="sz-stand pruefen">nicht in Ihrer Kundenliste, wird beim Schreiben neu angelegt</small>;
  return null;
}

// Kunde wählen: aus der Kundenliste suchen (wie in der Rechnung, KundeWahl.suche) oder einen neuen Namen eintippen.
// Gewählt: Name mit „ändern“.
function KundeFeld({
  kunden,
  wert,
  onChange,
  label,
  ki = false,
}: {
  kunden: Kunde[];
  wert: KundeWahlWert;
  onChange(w: KundeWahlWert): void;
  label: string;
  ki?: boolean;
}) {
  const [text, setText] = useState(wert.neu ?? '');
  useEffect(() => setText(wert.neu ?? ''), [wert.neu, wert.kundeId]);
  const gewaehlt = wert.kundeId ? kunden.find((k) => k.id === wert.kundeId) : undefined;
  const treffer = useMemo(() => suche(kunden, text), [kunden, text]);
  if (gewaehlt) {
    return (
      <span className={`sz-kw${ki ? ' sz-ki' : ''}`}>
        <b>{gewaehlt.name}</b>
        {gewaehlt.nummer != null && <span className="muted num"> Nr. {gewaehlt.nummer}</span>}
        <button type="button" className="linkbtn" aria-label={`${label}: anderen Kunden wählen`} onClick={() => onChange({ kundeId: null, neu: null })}>
          ändern
        </button>
      </span>
    );
  }
  return (
    <span className={`sz-such${ki && wert.neu ? ' sz-ki' : ''}`}>
      <VorschlagInput
        value={text}
        vorschlaege={treffer.map((k) => ({
          key: k.id!,
          label: (
            <>
              {k.nummer != null && <span className="kw-nr num">{k.nummer}</span>} {k.name}
            </>
          ),
          sub: [k.street, `${k.zip} ${k.city}`].filter((x) => x.trim()).join(', '),
        }))}
        onPick={(id) => onChange({ kundeId: id, neu: null })}
        onChange={(t) => {
          setText(t);
          onChange({ kundeId: null, neu: t.trim() ? t : null });
        }}
        inputProps={{ 'aria-label': label, placeholder: kunden.length ? 'Kunde suchen: Name, Nummer oder Ort' : 'Name des Kunden' }}
      />
    </span>
  );
}
